import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MIN_UPDATE_INTERVAL_MS } from '~/containers/viewer/viewport/previewRoiNumbers';
import {
    PreviewVolume,
    previewVolumeOf,
} from '~/engine/tool/actions/contouring-actions/RoiPreviewRenderer';
import { GlOperationGuard } from '~/engine/util/algorithms/sdf/utils';
import {
    CrossSectionPreviewHandle,
    crossSectionPreviewHandleState,
} from '~/state/atoms/toolsAtoms';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';

const MAX_BUSY_DEFERRALS = 8;

/** The uncommitted Cross-Section shape as a GPU-ready volume, or null when nothing is previewed. */
export function useCrossSectionPreviewVolume(): PreviewVolume | null {
    const published = useRecoilValue(crossSectionPreviewHandleState);
    const isPreviewOpen = useRecoilValue(roiPreview3DOpenState);
    const handle = isPreviewOpen ? published : null;
    const [volume, setVolume] = useState<PreviewVolume | null>(null);
    const handleRef = useRef<CrossSectionPreviewHandle | null>(handle);
    handleRef.current = handle;
    const deferralsRef = useRef(0);
    const busyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const convert = useCallback((): void => {
        const current = handleRef.current;
        if (!current) {
            setVolume(null);
            return;
        }
        if (GlOperationGuard.isActive) {
            if (deferralsRef.current >= MAX_BUSY_DEFERRALS) {
                // Cleared, not left standing: a stale solid would disagree with the contours unseen.
                deferralsRef.current = 0;
                console.warn(
                    'RoiPreviewPane: gave up reading the cross-section ' +
                        'preview; the GPU pipeline stayed busy'
                );
                setVolume(null);
                return;
            }
            deferralsRef.current++;
            if (busyTimerRef.current === null) {
                busyTimerRef.current = setTimeout(() => {
                    busyTimerRef.current = null;
                    convert();
                }, MIN_UPDATE_INTERVAL_MS);
            }
            return;
        }
        deferralsRef.current = 0;
        try {
            const texture = current.texture;
            if (texture.isEmpty) {
                setVolume(null);
                return;
            }
            setVolume({
                ...previewVolumeOf(texture, current.color),
                ghostWhenOccluded: true,
            });
        } catch (error) {
            console.warn(
                'RoiPreviewPane: reading the cross-section preview failed',
                error
            );
            setVolume(null);
        }
    }, []);
    const throttledConvert = useMemo(
        () => throttle(convert, MIN_UPDATE_INTERVAL_MS),
        [convert]
    );
    useEffect(() => {
        if (!handle) {
            // Cleared at once: after Apply a green copy must not linger beside the committed shape.
            throttledConvert.cancel();
            deferralsRef.current = 0;
            setVolume(null);
            return;
        }
        throttledConvert();
    }, [handle, throttledConvert]);
    useEffect(
        () => () => {
            throttledConvert.cancel();
            if (busyTimerRef.current !== null) {
                clearTimeout(busyTimerRef.current);
                busyTimerRef.current = null;
            }
        },
        [throttledConvert]
    );
    return volume;
}
